/*
 * Team access (docs/api.md, "Team access"; docs/access-permissions.md): who
 * reaches which sites, at which level on each environment, with which site
 * abilities. The org role still decides org-wide things. Screens ask these
 * routes, and the `access` the API puts on sites and environments, rather than
 * working anything out from roles.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrg } from "../app/org";
import { api } from "./client";
import { keys } from "./keys";
import type { ClientAccess, Role, Via } from "./types";

export type Level = "none" | "view" | "deploy" | "manage";
export type EnvLevels = { dev: Level; test: Level; live: Level; extra: Level; environments?: Record<string, Level> };
/**
 * `data`: the database's data (browsing, changing, export, import, its users) where they also manage; `wpSignIn`:
 * one-click WordPress admin on every environment they can see.
 */
export type Abilities = { settings: boolean; email: boolean; createEnvironments: boolean; data: boolean; wpSignIn: boolean };
export type Preset = "full" | "developer" | "developerNoLive" | "viewer" | "client" | "mailOnly" | "custom";
export type SiteScope = "all" | "sites";

export type SiteGrant = { siteId: string; preset: Preset; levels?: EnvLevels; abilities?: Abilities };

/** One group's grant on a site, as a person's and a site's Access show where access comes from. */
export type GroupPart = { groupId: string; groupName: string; via: "all" | "site" | "tag"; tag: string | null; preset: Preset; levels: EnvLevels; abilities: Abilities };
/** The person's own part of their access to a site; null when it's only through groups. */
export type OwnPart = { preset: Preset; levels: EnvLevels; abilities: Abilities } | null;

export type MemberSiteAccess = SiteGrant & {
  siteName: string;
  levels: EnvLevels;
  abilities: Abilities;
  deleteSite: boolean;
  /** "All sites as a developer", "Given access by Ryan on Oct 20". */
  reason: string;
  grantedBy: { id: string; name: string } | null;
  grantedAt: string | null;
  /** The site's extra environments (coming with the next API release), so each can have its own level here too. */
  extraEnvironments?: { id: string; slug?: string; label?: string }[];
  /** Where it comes from: `levels` and `abilities` add up their own (`own`, what this row changes) and each group's. */
  own?: OwnPart;
  groups?: GroupPart[];
};

/** A person's group and what it gives (in this org's sites). */
export type MemberGroup = {
  id: string;
  name: string;
  description: string | null;
  gives: { kind: "all" | "site" | "tag"; siteId: string | null; siteName: string | null; tag: string | null; preset: Preset; levels: EnvLevels; abilities: Abilities }[];
};

export type MemberAccess = {
  userId: string;
  name: string;
  email: string;
  role: Role;
  via: Via;
  siteScope: SiteScope;
  sites: MemberSiteAccess[];
  /** Their groups here (a member's own org's), each with what it gives. */
  groups?: MemberGroup[];
  /** "Maya can deploy to Dev and Test on 3 sites, and can't see Live.", counting their groups too. */
  summary: string;
  /** A client person's switches (Clients › What clients can do). */
  client?: ClientAccess;
  /** Whether the caller may change it. */
  editable: boolean;
};

export type SitePerson = { userId: string; name: string; email: string; role: Role; via: Via; preset: Preset; levels: EnvLevels; abilities: Abilities; reason: string; editable: boolean; own?: OwnPart; groups?: GroupPart[] };
/** A group that gives something on a site: its grants here (the site's own, and its every-site and tag ones), added up. */
export type SiteGroup = {
  groupId: string;
  name: string;
  orgId: string;
  orgName: string;
  via: Via;
  memberCount: number;
  preset: Preset;
  levels: EnvLevels;
  abilities: Abilities;
  grants: { kind: "all" | "site" | "tag"; tag: string | null; preset: Preset; levels: EnvLevels; abilities: Abilities }[];
  editable: boolean;
};
/** An invite that gives access to the site, with what a people row shows (an older API: the first four only). */
export type SiteInviteAccess = { id: string; email: string; role: Role; grant: SiteGrant; expiresAt?: string; createdAt?: string; lastSentAt?: string | null; invitedBy?: { id: string; name: string } | null; emailStatus?: string | null; emailError?: string | null; groups?: { id: string; name: string }[] };

export type MyAccess = { role: Role; siteScope: SiteScope; groups?: { id: string; name: string }[]; sites: { siteId: string; levels: EnvLevels; abilities: Abilities; deleteSite: boolean }[] };

/** An invite's access, when it carries one. */
export type InviteAccess = { siteScope: "all" } | { siteScope: "sites"; sites: SiteGrant[] };

export const accessKey = (orgId: string) => ["org", orgId, "access"] as const;

export function useMemberAccess(userId: string | null) {
  const o = useOrg();
  return useQuery({
    queryKey: [...accessKey(o.id), "member", userId],
    queryFn: ({ signal }) => api.get<MemberAccess>(`/orgs/${o.id}/members/${userId}/access`, signal),
    enabled: !!userId,
  });
}

export function useSiteAccess(siteId: string, enabled = true) {
  const o = useOrg();
  return useQuery({
    queryKey: [...accessKey(o.id), "site", siteId],
    queryFn: ({ signal }) => api.get<{ people: SitePerson[]; groups?: SiteGroup[]; invites: SiteInviteAccess[]; canGive?: boolean; groupsToGive?: { id: string; name: string; orgName: string }[] }>(`/orgs/${o.id}/sites/${siteId}/access`, signal),
    enabled,
    retry: false,
  });
}

/** The caller's own access in this org: every site's levels and abilities. */
export function useMyAccess() {
  const o = useOrg();
  return useQuery({
    queryKey: [...accessKey(o.id), "me"],
    queryFn: ({ signal }) => api.get<MyAccess>(`/me/access?orgId=${o.id}`, signal),
    staleTime: 30_000,
  });
}

function useAccessWrite<V, R>(fn: (orgId: string, v: V) => Promise<R>) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(o.id, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: accessKey(o.id) });
      void qc.invalidateQueries({ queryKey: keys.members(o.id) });
    },
  });
}

/**
 * A member's role (`PATCH /orgs/:orgId/members/:userId`). Admins; only owners make or change owners; an org keeps
 * an owner (409 with the reason). A lower role also caps their site access at the new role's default.
 */
export function useChangeRole() {
  return useAccessWrite((orgId, v: { userId: string; role: Role }) => api.patch<{ ok: true; role: Role }>(`/orgs/${orgId}/members/${v.userId}`, { role: v.role }));
}

/** Replace a person's access: All sites, or only these sites. */
export function useSetMemberAccess(userId: string) {
  return useAccessWrite((orgId, v: { siteScope: "all" } | { siteScope: "sites"; sites: SiteGrant[] }) => api.put<MemberAccess>(`/orgs/${orgId}/members/${userId}/access`, v));
}

/** One site's grant for a person, added or changed. */
export function useSetSiteGrant() {
  return useAccessWrite((orgId, v: { userId: string; grant: SiteGrant }) => {
    const { siteId, ...rest } = v.grant;
    return api.put<MemberAccess>(`/orgs/${orgId}/members/${v.userId}/access/sites/${siteId}`, rest);
  });
}

export function useRemoveSiteGrant() {
  return useAccessWrite((orgId, v: { userId: string; siteId: string }) => api.del<MemberAccess>(`/orgs/${orgId}/members/${v.userId}/access/sites/${v.siteId}`, {}));
}

/** "Give someone access": an existing member, or a group (its grant on this site). */
export function useGiveSiteAccess(siteId: string) {
  return useAccessWrite((orgId, v: ({ userId: string } | { groupId: string }) & Omit<SiteGrant, "siteId">) => api.post<{ people: SitePerson[]; groups?: SiteGroup[] }>(`/orgs/${orgId}/sites/${siteId}/access`, v));
}

/* ── Reading the access the API puts on sites and environments ──── */

const RANK: Record<Level, number> = { none: 0, view: 1, deploy: 2, manage: 3 };

/** Whether a level allows `need`. An answer from an older API without levels counts as allowed; the API checks every request anyway. */
export const allows = (have: Level | null | undefined, need: Exclude<Level, "none">) => (have ? RANK[have] >= RANK[need] : true);

/** "Deploying to Live needs deploy access to Live.": the same words the API answers with. */
export const needsWords = (what: string, need: "deploy" | "manage", envLabel: string) => `${what} needs ${need} access to ${envLabel}.`;

const FULL_ABILITIES: Abilities = { settings: true, email: true, createEnvironments: true, data: true, wpSignIn: false };

/**
 * What the caller can do on one site: their level on each environment (from the environment itself when it's
 * there, else from /me/access, so a hidden environment is told apart from one that doesn't exist), the site's
 * abilities, and whether they may delete it. From the API's answers only.
 */
export function useSiteCan(site: { id: string; access?: { abilities: Abilities; deleteSite: boolean; deployAll?: boolean }; environments?: { name: string; access?: Level }[] }) {
  const me = useMyAccess();
  const mine = me.data?.sites.find((s) => s.siteId === site.id) ?? null;
  const levelOf = (name: "dev" | "test" | "live"): Level | undefined => site.environments?.find((e) => e.name === name)?.access ?? mine?.levels[name] ?? undefined;
  const abilities = site.access?.abilities ?? mine?.abilities ?? FULL_ABILITIES;
  const deployAll = site.access?.deployAll ?? (mine ? [mine.levels.dev, mine.levels.test, mine.levels.live, mine.levels.extra, ...Object.values(mine.levels.environments ?? {})].every((l) => allows(l, "deploy")) : true);
  return {
    levelOf,
    /** Hidden from the caller (as opposed to not existing). */
    hidden: (name: "dev" | "test" | "live") => levelOf(name) === "none",
    abilities,
    deleteSite: site.access?.deleteSite ?? mine?.deleteSite ?? false,
    /** What runs on every environment (the app's settings, the repository): the site's settings and deploy on each. */
    changesWhatRuns: abilities.settings && deployAll,
  };
}

/** Whether an environment is outside the caller's access on a site (as opposed to not existing), for lists without environments. */
export function useEnvHidden() {
  const me = useMyAccess();
  return (siteId: string, name: "dev" | "test" | "live" = "live") => me.data?.sites.find((s) => s.siteId === siteId)?.levels[name] === "none";
}

/* ── What clients can do (an agency's default for its client people) ── */

export type ClientDefaults = {
  defaults: ClientAccess;
  builtin: ClientAccess;
  /** Each client: its own default where it differs (null: the agency's), what that comes to, its client people, and how many of them differ from it. */
  clients: { clientId: string; name: string; slug: string; own: ClientAccess | null; effective: ClientAccess; people: number; behind: number }[];
  /** Client people anywhere whose switches differ from their client's default ("Apply to everyone now" changes them). */
  behind: number;
};

const clientDefaultsKey = (orgId: string) => ["org", orgId, "client-defaults"] as const;

/** `GET /orgs/:orgId/client-defaults`: an agency's admins. */
export function useClientDefaults(enabled = true) {
  const o = useOrg();
  return useQuery({ queryKey: clientDefaultsKey(o.id), queryFn: ({ signal }) => api.get<ClientDefaults>(`/orgs/${o.id}/client-defaults`, signal), enabled, retry: false });
}

function useClientDefaultsWrite<V>(fn: (orgId: string, v: V) => Promise<ClientDefaults & { changed?: number }>) {
  const o = useOrg();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(o.id, v),
    onSuccess: (r) => {
      qc.setQueryData(clientDefaultsKey(o.id), r);
      void qc.invalidateQueries({ queryKey: keys.members(o.id) });
    },
  });
}

/** The agency's default for new client people. */
export function useSetClientDefaults() {
  return useClientDefaultsWrite((orgId, defaults: ClientAccess) => api.put<ClientDefaults>(`/orgs/${orgId}/client-defaults`, { defaults }));
}

/** One client's own default (null: the agency's again). */
export function useSetClientOverride() {
  return useClientDefaultsWrite((orgId, v: { clientId: string; defaults: ClientAccess | null }) => api.put<ClientDefaults>(`/orgs/${orgId}/clients/${v.clientId}/client-defaults`, { defaults: v.defaults }));
}

/** "Apply to everyone now": the client people already there get their client's default (one client, or all). */
export function useApplyClientDefaults() {
  return useClientDefaultsWrite((orgId, v: { clientId?: string }) => api.post<ClientDefaults & { changed: number }>(`/orgs/${orgId}/client-defaults/apply`, v));
}
